import React, { useState } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { AlertCircle, Plus, Trash2, ChevronDown, ChevronUp } from 'react-feather';
import { FormTitle, FormSubtitle, FormGroup, FormRow, Label, StyledInput, StyledSelect, CheckboxLabel, ErrorText, SecondaryButton } from '../styles';
import { PhoneInput } from '../../../../common/PhoneInput';

const Step3AuthorizedSigners = () => {
    const { register, watch, control, formState: { errors } } = useFormContext();
    const { fields, append, remove } = useFieldArray({
        control,
        name: "signers"
    });

    const [expandedIndex, setExpandedIndex] = useState(0);
    const signers = watch('signers');

    return (
        <div>
            <FormTitle>Authorized Signatories</FormTitle>
            <FormSubtitle>All individuals with 25%+ ownership (direct or indirect) must be identified. (Max 4)</FormSubtitle>

            {fields.map((field, index) => {
                const idType = signers?.[index]?.signerIdType;
                const isPEP = signers?.[index]?.isPEP;
                const fieldErrors = errors?.signers?.[index];
                const isExpanded = expandedIndex === index;
                const hasErrors = fieldErrors && Object.keys(fieldErrors).length > 0;

                return (
                    <div key={field.id} style={{ 
                        border: '1px solid rgba(255,255,255,0.08)', 
                        borderRadius: '12px', 
                        marginBottom: '16px',
                        backgroundColor: 'rgba(255,255,255,0.02)',
                        overflow: 'hidden'
                    }}>
                        {/* Accordion Header */}
                        <div 
                            onClick={() => setExpandedIndex(isExpanded ? null : index)}
                            style={{ 
                                padding: '16px 20px', 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                cursor: 'pointer',
                                backgroundColor: isExpanded ? 'rgba(255,255,255,0.04)' : 'transparent',
                                borderBottom: isExpanded ? '1px solid rgba(255,255,255,0.08)' : 'none'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: hasErrors ? '#ef4444' : 'inherit' }}>
                                    Signer #{index + 1}: {signers?.[index]?.signerName || 'New Signer'}
                                </h4>
                                {hasErrors && <AlertCircle size={14} style={{ color: '#ef4444' }} />}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                {fields.length > 1 && (
                                    <Trash2 
                                        size={16} 
                                        style={{ color: '#ef4444', opacity: 0.7 }} 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            remove(index);
                                            if (expandedIndex === index) setExpandedIndex(null);
                                        }} 
                                    />
                                )}
                                {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                            </div>
                        </div>

                        {/* Accordion Content */}
                        {isExpanded && (
                            <div style={{ padding: '24px' }}>
                                <FormRow>
                                    <FormGroup>
                                        <Label>Full Legal Name</Label>
                                        <StyledInput 
                                            type="text" 
                                            {...register(`signers.${index}.signerName`)} 
                                            placeholder="Jane Doe" 
                                            $error={!!fieldErrors?.signerName} 
                                        />
                                        {fieldErrors?.signerName && <ErrorText>{fieldErrors.signerName.message}</ErrorText>}
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Title / Position</Label>
                                        <StyledInput 
                                            type="text" 
                                            {...register(`signers.${index}.signerTitle`)} 
                                            placeholder="Head of Treasury / Director" 
                                            $error={!!fieldErrors?.signerTitle} 
                                        />
                                        {fieldErrors?.signerTitle && <ErrorText>{fieldErrors.signerTitle.message}</ErrorText>}
                                    </FormGroup>
                                </FormRow>

                                <FormRow>
                                    <FormGroup>
                                        <Label>Ownership %</Label>
                                        <StyledInput
                                            type="number"
                                            {...register(`signers.${index}.ownershipPercentage`, { valueAsNumber: true })}
                                            placeholder="e.g. 50"
                                            min="0"
                                            max="100"
                                            $error={!!fieldErrors?.ownershipPercentage}
                                        />
                                        {fieldErrors?.ownershipPercentage && <ErrorText>{fieldErrors.ownershipPercentage.message}</ErrorText>}
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Date of Birth</Label>
                                        <StyledInput type="date" {...register(`signers.${index}.signerDob`)} $error={!!fieldErrors?.signerDob} />
                                        {fieldErrors?.signerDob && <ErrorText>{fieldErrors.signerDob.message}</ErrorText>}
                                    </FormGroup>
                                </FormRow>

                                <FormRow>
                                    <FormGroup>
                                        <Label>Business Email</Label>
                                        <StyledInput 
                                            type="email" 
                                            {...register(`signers.${index}.signerEmail`)} 
                                            placeholder="jane@examplebank.com" 
                                            $error={!!fieldErrors?.signerEmail} 
                                        />
                                        {fieldErrors?.signerEmail && <ErrorText>{fieldErrors.signerEmail.message}</ErrorText>}
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Phone Number</Label>
                                        <PhoneInput
                                            dialCodeName={`signers.${index}.signerPhoneDialCode`}
                                            numberName={`signers.${index}.signerPhone`}
                                            register={register}
                                            errors={errors}
                                            index={index}
                                            arrayName="signers"
                                        />
                                        {fieldErrors?.signerPhone && <ErrorText>{fieldErrors.signerPhone.message}</ErrorText>}
                                    </FormGroup>
                                </FormRow>

                                <FormRow>
                                    <FormGroup>
                                        <Label>Government ID Type</Label>
                                        <StyledSelect {...register(`signers.${index}.signerIdType`)} $error={!!fieldErrors?.signerIdType}>
                                            <option value="">Select type</option>
                                            <option value="passport">Passport</option>
                                            <option value="national_id">National ID Card</option>
                                            <option value="driver_license">Driver's License</option>
                                        </StyledSelect>
                                        {fieldErrors?.signerIdType && <ErrorText>{fieldErrors.signerIdType.message}</ErrorText>}
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>ID Number</Label>
                                        <StyledInput 
                                            type="text" 
                                            {...register(`signers.${index}.signerIdNumber`)} 
                                            placeholder="Document Number" 
                                            $error={!!fieldErrors?.signerIdNumber} 
                                        />
                                        {fieldErrors?.signerIdNumber && <ErrorText>{fieldErrors.signerIdNumber.message}</ErrorText>}
                                    </FormGroup>

                                    {idType === 'passport' && (
                                        <FormGroup>
                                            <Label>Issuing Country</Label>
                                            <StyledInput
                                                type="text"
                                                {...register(`signers.${index}.signerIdIssuingCountry`)}
                                                placeholder="e.g. Mexico"
                                                $error={!!fieldErrors?.signerIdIssuingCountry}
                                            />
                                            {fieldErrors?.signerIdIssuingCountry && <ErrorText>{fieldErrors.signerIdIssuingCountry.message}</ErrorText>}
                                        </FormGroup>
                                    )}
                                </FormRow>

                                <FormGroup>
                                    <Label>Home / Personal Address</Label>
                                    <StyledInput 
                                        type="text" 
                                        {...register(`signers.${index}.signerAddress`)} 
                                        placeholder="Street Address" 
                                        $error={!!fieldErrors?.signerAddress} 
                                    />
                                    {fieldErrors?.signerAddress && <ErrorText>{fieldErrors.signerAddress.message}</ErrorText>}
                                </FormGroup>

                                <FormRow>
                                    <FormGroup>
                                        <Label>City</Label>
                                        <StyledInput 
                                            type="text" 
                                            {...register(`signers.${index}.signerCity`)} 
                                            placeholder="City" 
                                            $error={!!fieldErrors?.signerCity} 
                                        />
                                        {fieldErrors?.signerCity && <ErrorText>{fieldErrors.signerCity.message}</ErrorText>}
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>State / Province</Label>
                                        <StyledInput 
                                            type="text" 
                                            {...register(`signers.${index}.signerState`)} 
                                            placeholder="State" 
                                            $error={!!fieldErrors?.signerState} 
                                        />
                                        {fieldErrors?.signerState && <ErrorText>{fieldErrors.signerState.message}</ErrorText>}
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>ZIP / Postal Code</Label>
                                        <StyledInput 
                                            type="text" 
                                            {...register(`signers.${index}.signerZipCode`)} 
                                            placeholder="ZIP Code" 
                                            $error={!!fieldErrors?.signerZipCode} 
                                        />
                                        {fieldErrors?.signerZipCode && <ErrorText>{fieldErrors.signerZipCode.message}</ErrorText>}
                                    </FormGroup>
                                </FormRow>

                                <FormGroup>
                                    <Label>Country of Residence</Label>
                                    <StyledInput 
                                        type="text" 
                                        {...register(`signers.${index}.signerCountry`)} 
                                        placeholder="e.g. United States" 
                                        $error={!!fieldErrors?.signerCountry} 
                                    />
                                    {fieldErrors?.signerCountry && <ErrorText>{fieldErrors.signerCountry.message}</ErrorText>}
                                </FormGroup>

                                <CheckboxLabel>
                                    <input type="checkbox" {...register(`signers.${index}.isPEP`)} />
                                    <span>This person is a Politically Exposed Person (PEP) or is affiliated with one.</span>
                                </CheckboxLabel>

                                {isPEP && (
                                    <FormGroup style={{ marginTop: '16px' }}>
                                        <Label>PEP Description</Label>
                                        <StyledInput
                                            as="textarea"
                                            rows="3"
                                            {...register(`signers.${index}.pepDetails`)}
                                            placeholder="Describe the political position or affiliation..."
                                            style={{ resize: 'vertical', minHeight: '80px' }}
                                            $error={!!fieldErrors?.pepDetails}
                                        />
                                        {fieldErrors?.pepDetails && <ErrorText>{fieldErrors.pepDetails.message}</ErrorText>}
                                    </FormGroup>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            {fields.length < 4 && (
                <SecondaryButton 
                    type="button" 
                    onClick={() => {
                        append({
                            signerName: '',
                            signerTitle: '',
                            ownershipPercentage: '',
                            signerDob: '',
                            signerIdType: '',
                            signerIdNumber: '',
                            signerIdIssuingCountry: '',
                            signerEmail: '',
                            signerPhoneDialCode: '+1',
                            signerPhone: '',
                            signerAddress: '',
                            signerCity: '',
                            signerState: '',
                            signerZipCode: '',
                            signerCountry: '',
                            isPEP: false,
                            pepDetails: ''
                        });
                        setExpandedIndex(fields.length); // Expand the newly added one
                    }}
                    style={{ width: '100%', justifyContent: 'center', marginBottom: '24px', marginTop: '8px' }}
                >
                    <Plus size={18} /> Add Another Authorized Signatory
                </SecondaryButton>
            )}

            {errors?.signers?.root && (
                <ErrorText style={{ textAlign: 'center', marginBottom: '16px', fontSize: '14px' }}>
                    {errors.signers.root.message}
                </ErrorText>
            )}

            <div style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'flex-start',
                marginTop: '16px',
                padding: '14px 16px',
                borderRadius: '10px',
                backgroundColor: 'rgba(99, 102, 241, 0.06)',
                border: '1px solid rgba(99, 102, 241, 0.2)',
            }}>
                <AlertCircle size={16} style={{ color: '#6366f1', flexShrink: 0, marginTop: '2px' }} />
                <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.5', color: 'inherit', opacity: 0.8 }}>
                    All authorized signatories are subject to OFAC and sanctions screening. By submitting, you authorize NYBANQ to verify identity through its compliance partners.
                </p>
            </div>
        </div>
    );
};

export default Step3AuthorizedSigners;
